import React, {useState, useEffect, useCallback, useContext} from 'react';
import CustomInput from '../../custom-input';
import Info from './info';
import Result from './result';
import ProgressWithHint from '../../../components/progress-hint';
import {toolRequest, request} from '../../../requests';
import WebSocket from 'isomorphic-ws';
import {
  validURL,
  returnSocketLink,
  freeToolsErrors,
  deviceOptions,
  screenShotDelayOptions,
  ERROR_MESSAGES,
} from '../../../helpers/constants';
import GoToLoginPage from '../tools-login';
import LimitReached from '../limit-modal';
import {UserContext, UserType} from '../../../helpers/contexts/user-context';
import Button from '../../button';
import Select from '../../../components/select';

import {Link} from 'gatsby';
import './styles.scss';
import {HexomaticUserType} from '../../../helpers/contexts/hexomatic-user';
import {useQuery} from '@apollo/react-hooks';
import {GET_HEXOMATIC_USER} from '../../../graphql/queries';
import {getHintPopupPerTool} from '../../../helpers';

const ScreenshotCaptureWrapper = () => {
  const hintName = 'free-tools-hint-screenshot';
  const {user} = useContext(UserContext);
  //----------------State----------------
  const [value, setValue] = useState('');
  const [delay, setDelay] = useState({label: 5, value: 5});
  const [socket, setSocket] = useState<any>(null);
  const [error, setError] = useState('');
  const [limit, setLimit] = useState('');
  const [socketId, setSocketId] = useState('');
  const [result, setResult] = useState<any[] | string>('');
  const [loading, setLoading] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [show, setShow] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [required, setRequired] = useState(false);
  const [clientId, setClientId] = useState('');
  const [selectedDevices, setSelectedDevices] = useState<{label: string; value: string}>({
    label: 'Laptop Large',
    value: 'LAPTOP_LARGE',
  });
  const [mainUser, setUser] = useState<null | UserType>(null);
  const [hexomaticUser, setHexomaticUser] = useState<null | HexomaticUserType>(null);

  const {data: hexomaticUserData} = useQuery(GET_HEXOMATIC_USER, {
    fetchPolicy: 'no-cache',
    skip: !!getHintPopupPerTool(hintName),
  });
  //----------useEffect-------------------
  useEffect(() => {
    if (hexomaticUserData && hexomaticUserData.HexomaticUser && hexomaticUserData.HexomaticUser.getByLegacyId) {
      setHexomaticUser(hexomaticUserData.HexomaticUser.getByLegacyId);
    }
    if (hexomaticUserData && hexomaticUserData.User && hexomaticUserData.User.get) {
      setUser(hexomaticUserData.User.get);
    }
  }, [hexomaticUserData]);

  useEffect(() => {
    socketStart();
  }, []);

  useEffect(() => {
    error === 'URL is not valid' && setError('');
  }, [value]);

  useEffect(() => {
    if (!socket) return;
    socket.onerror = function() {
      console.log('Connection Error');
    };

    socket.onopen = function() {
      console.log('WebSocket Client Connected');
    };

    socket.onclose = () => {
      socketStart();
    };

    return () => {
      socket.close();
    };
  }, [socket]);
  useEffect(() => {
    if (!socket) return;
    socket.onmessage = function(e: any) {
      const data = JSON.parse(e.data);
      if (data && data.clientId && data.clientId !== clientId) {
        setClientId(data.clientId);
        return;
      }
      if (data && data.error && freeToolsErrors.includes(data.error)) {
        setLimit(data.error);
        setShow(false);
        return;
      }
      if (data && data.socketId !== socketId) return;

      setResult(Object.values(data).filter(item => typeof item === 'object'));
      setShow(false);
      setSocketId('');
    };
  }, [socket, clientId, socketId]);
  //---------------Functions----------------------

  const handleClick = useCallback(async () => {
    if (!value || !value.trim()) {
      return;
    }
    if (!validURL(value)) {
      setError('URL is not valid');
      return;
    }
    if (!selectedDevices.value) {
      setRequired(true);
      return;
    }
    setRequired(false);
    setError('');
    setShow(false);
    setResult('');
    if (!getHintPopupPerTool(hintName) && user && user.id) {
      setShowCreditsModal(true);
      return;
    }
    try {
      setLoading(true);
      const res = await toolRequest('device-screenshot', value, {
        clientId,
        devices: [selectedDevices.value],
        delay: delay.value,
        adBlock: false,
        requestSource: 'free_tools',
      });
      if (res.status === 403) {
        setLimitReached(true);
        setLoading(false);
        setLoading(false);
        setSocketId('');
        return;
      }
      if (res?.data?.socketId) {
        setSocketId(res.data.socketId);
        setResult('');
        setShow(true);
      } else if (res.errorData) {
        if (ERROR_MESSAGES[res.errorData.message]) {
          setLimit(ERROR_MESSAGES[res.errorData.message]);
        } else {
          setError('Something went wrong. Please try again.');
        }
      }
      setLoading(false);
    } catch (error) {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  }, [value, selectedDevices, setError]);

  const socketStart = () => {
    //@ts-ignore
    const newSocket = new WebSocket(returnSocketLink());
    setSocket(newSocket);
  };

  const getResult = async () => {
    if (!socketId) return;
    try {
      //setLoading(true);
      const res: any = await request(`result/${socketId}`);
      if (
        res &&
        res.data &&
        res.data.result &&
        res.data.result.error &&
        freeToolsErrors.includes(res.data.result.error)
      ) {
        setLimit(res.data.result.error);
        setShow(false);
        setLoading(false);
        setSocketId('');
        return;
      }
      if (res && res.data && res.data.result && res.data.result.message) {
        setError('Something went wrong.');
      }
      if (
        res &&
        res.data &&
        res.data.result &&
        Object.values(res.data.result).filter(item => typeof item === 'object')
      ) {
        setResult(Object.values(res.data.result).filter(item => typeof item === 'object'));
        setShow(false);
      }
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };
  const handleChangeModalState = () => setLimitReached(false);
  //-------------------------------------------------------------
  return (
    <div className="d-block m-auto px-2">
      {
        <div className="d-flex pb-2" style={{maxWidth: '785px', margin: 'auto'}}>
          <Link className="n-wrap" to="/tools">
            ← Back to tools
          </Link>
          <Link to="/tools/history/#screenshot-capture" className="n-wrap ml-auto">
            See history →
          </Link>
        </div>
      }
      <div className="mx-auto screenshot-wrapper mb-2">
        <CustomInput error={error} onChange={setValue} value={value} placeholder="Add URL" disabled={loading || show} />
        <div className="d-flex align-items-end justify-content-between ">
          <div className="d-sm-flex">
            <div className="mr-2 device-wrapper">
              <span className="label secondary-color">Devices</span>
              <Select
                options={deviceOptions}
                onChange={setSelectedDevices}
                value={selectedDevices}
                disabled={loading}
                empty={required && !selectedDevices.value}
                placeHolder="Select"
              />
            </div>
            <div className="delay-wrapper">
              <label className="mb-0">Delay (seconds)</label>
              <Select
                options={screenShotDelayOptions}
                onChange={setDelay}
                value={delay}
                disabled={loading}
                empty={required && !delay}
              />
            </div>
          </div>

          <Button onClick={handleClick} disabled={loading || show}>
            Start
          </Button>
        </div>
      </div>
      {result && result.length > 0 && <Result result={result} scanUrl={value} selectedDevice={selectedDevices.value} />}
      <div className={`mx-auto col-12 my-1 ${socketId && (!result || !result.length) ? 'visible' : 'invisible'}`}>
        {show && <ProgressWithHint func={getResult} />}
      </div>
      <Info />
      {limitReached && <GoToLoginPage toolName="Screenshot capture" setOpen={handleChangeModalState} />}
      {limit && <LimitReached text={limit} setLimit={setLimit} />}
      {showCreditsModal && (
        <LimitReached
          hint_name={hintName}
          defaultModal
          hideModal={setShowCreditsModal}
          automation_credits={1}
          confirm={handleClick}
          user={mainUser}
          hexomaticUser={hexomaticUser}
        />
      )}
    </div>
  );
};

export default ScreenshotCaptureWrapper;
